import type { TemplateField } from 'shared/api/templateService'

export type DependencyValueFormat = 'string' | 'date' | 'time' | 'datetime'

export interface DependencyRuleConfig {
  parameterName?: string
  requiredForLoad?: boolean
  timeFieldCode?: string
  combineWithFieldCode?: string
  valueFormat?: DependencyValueFormat
}

export interface AdditionalDependencyRule extends DependencyRuleConfig {
  fieldCode: string
}

export type InformationMessageVariant = 'info' | 'warning' | 'danger'

export interface OptionInformationMessageConfig {
  code?: string
  codes?: string[]
  value?: string
  values?: string[]
  title?: string
  message?: string
  messages?: string[]
  variant?: InformationMessageVariant | string
}

export interface ResolvedInformationMessage {
  key: string
  title?: string
  messages: string[]
  variant: InformationMessageVariant
}

export type LegacyDependencyRuleShape = {
  timeField?: string
  combineWith?: string
}

export interface ParsedFieldConfig {
  dependsOn?: string
  dependencyConfig?: DependencyRuleConfig
  extraDependencies?: AdditionalDependencyRule[]
  controlType?: string
  renderAs?: string
  inputType?: string
  optionMessages?: OptionInformationMessageConfig[]
  informationMessages?: Record<string, unknown>
}

export type FieldInputType = 'single' | 'multiple'

export type FieldValueResolver = (fieldCode?: string) => string | undefined

const IMPLICIT_TIME_FIELD_COMPANIONS: Record<string, string> = {
  STDATE: 'STTIME',
  ENDATE: 'ENTIME',
  STARTDATE: 'STTIME',
  START_DATE: 'STTIME',
  ENDDATE: 'ENTIME',
  END_DATE: 'ENTIME',
}

const shouldAutoMergeDateTime = (rule: AdditionalDependencyRule): boolean => {
  if (rule.valueFormat === 'datetime') {
    return true
  }
  const param = rule.parameterName?.trim().toLowerCase()
  if (param && (param.endsWith('_at') || param.endsWith('_datetime'))) {
    return true
  }
  if (rule.fieldCode && IMPLICIT_TIME_FIELD_COMPANIONS[rule.fieldCode.toUpperCase()]) {
    return true
  }
  return false
}

export const tryParseJson = <T = Record<string, unknown>>(value?: string | null): T | null => {
  if (!value || typeof value !== 'string') {
    return null
  }
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

export const resolveFieldInputType = (field: TemplateField): FieldInputType => {
  const fieldType = (field.fieldType ?? '').trim().toLowerCase()
  if (fieldType === 'multiselect') {
    return 'multiple'
  }

  const uiConfig = tryParseJson<Record<string, unknown>>(field.datasource?.uiConfigJson)
  const rawInputType = typeof uiConfig?.inputType === 'string' ? uiConfig.inputType.trim().toLowerCase() : ''
  return rawInputType === 'multiple' ? 'multiple' : 'single'
}

export const isSearchSelectMultiple = (field: TemplateField): boolean =>
  (field.fieldType ?? '').trim().toLowerCase() === 'search_select' && resolveFieldInputType(field) === 'multiple'

export const sanitizeFieldString = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export const resolveTemplateFieldKeys = (field: TemplateField): string[] => {
  const keys: string[] = []
  const code = field.code ?? ''
  if (code) {
    keys.push(code)
    const upper = code.toUpperCase()
    if (upper !== code) {
      keys.push(upper)
    }
  }
  if (field.fieldId && !keys.includes(field.fieldId)) {
    keys.push(field.fieldId)
  }
  return keys
}

export const TEMPLATE_FIELD_LABEL_KEY_PREFIX = '__LABEL__'

export const buildTemplateFieldLabelKey = (key: string): string =>
  `${TEMPLATE_FIELD_LABEL_KEY_PREFIX}${key}`

export const resolveTemplateFieldLabelKeys = (field: TemplateField): string[] =>
  resolveTemplateFieldKeys(field)
    .filter((key) => key.length > 0)
    .map((key) => buildTemplateFieldLabelKey(key))

const padTimeComponent = (value: number): string => value.toString().padStart(2, '0')

// Datasource functions expect ISO-like 24-hour timestamps, so convert UI inputs such as "05:00 AM".
const convertTo24HourFormat = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?(?:\s*(AM|PM))?$/i)
  if (!match) {
    return null
  }

  let hours = Number.parseInt(match[1] ?? '0', 10)
  const minutes = Number.parseInt(match[2] ?? '0', 10)
  const seconds = Number.parseInt(match[3] ?? '0', 10)
  const period = match[4]?.toUpperCase() ?? null

  if (period) {
    hours %= 12
    if (period === 'PM') {
      hours += 12
    }
  } else {
    hours %= 24
  }

  return `${padTimeComponent(hours)}:${padTimeComponent(minutes)}:${padTimeComponent(seconds)}`
}

export const mergeDateAndTimeValues = (
  dateValue?: string,
  timeValue?: string,
): string | undefined => {
  const datePart = sanitizeFieldString(dateValue)
  if (!datePart) {
    return undefined
  }

  const timePart = sanitizeFieldString(timeValue)
  if (timePart) {
    const normalizedTime = convertTo24HourFormat(timePart) ?? timePart
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return `${datePart}T${normalizedTime}`
    }
    return `${datePart} ${normalizedTime}`.trim()
  }

  return datePart
}

export const normalizeDateTimeString = (value: string): string => value.replace('T', ' ').replace(/\s+/g, ' ').trim()

export const parseFieldConfig = (field: TemplateField): ParsedFieldConfig | null => {
  if (!field.config) return null
  const trimmed = field.config.trim()
  if (!trimmed) return null
  try {
    return JSON.parse(trimmed) as ParsedFieldConfig
  } catch {
    return null
  }
}

const normalizeConfigValue = (value?: string | null): string =>
  (value ?? '').trim().toLowerCase()

const resolveInformationMessageVariant = (value: unknown): InformationMessageVariant => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (normalized === 'warning') {
    return 'warning'
  }
  if (normalized === 'danger' || normalized === 'error') {
    return 'danger'
  }
  return 'info'
}

const normalizeMessageList = (value: unknown): string[] => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }

  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item.length > 0)
}

const buildResolvedMessage = (
  key: string,
  rawValue: unknown,
  fallbackVariant: InformationMessageVariant = 'info',
): ResolvedInformationMessage | null => {
  if (typeof rawValue === 'string' || Array.isArray(rawValue)) {
    const messages = normalizeMessageList(rawValue)
    return messages.length > 0
      ? {
          key,
          messages,
          variant: fallbackVariant,
        }
      : null
  }

  if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
    return null
  }

  const candidate = rawValue as Record<string, unknown>
  const messages = normalizeMessageList(candidate.messages ?? candidate.message)
  if (messages.length === 0) {
    return null
  }

  const title =
    typeof candidate.title === 'string' && candidate.title.trim().length > 0
      ? candidate.title.trim()
      : undefined

  return {
    key,
    title,
    messages,
    variant: resolveInformationMessageVariant(candidate.variant ?? fallbackVariant),
  }
}

export const resolveFieldInformationMessages = (
  config: ParsedFieldConfig | null,
  selectedValues: string[],
): ResolvedInformationMessage[] => {
  if (!config || selectedValues.length === 0) {
    return []
  }

  const normalizedSelections = selectedValues
    .map((value) => value.trim())
    .filter((value) => value.length > 0)

  if (normalizedSelections.length === 0) {
    return []
  }

  const normalizedSelectionKeys = new Set(
    normalizedSelections.map((value) => normalizeConfigValue(value)),
  )

  const resolvedMessages: ResolvedInformationMessage[] = []

  for (const entry of config.optionMessages ?? []) {
    const entryValues = [
      ...(typeof entry.code === 'string' ? [entry.code] : []),
      ...(Array.isArray(entry.codes) ? entry.codes : []),
      ...(typeof entry.value === 'string' ? [entry.value] : []),
      ...(Array.isArray(entry.values) ? entry.values : []),
    ]
      .map((value) => value.trim())
      .filter((value) => value.length > 0)

    if (
      entryValues.length === 0 ||
      !entryValues.some((value) => normalizedSelectionKeys.has(normalizeConfigValue(value)))
    ) {
      continue
    }

    const resolved = buildResolvedMessage(
      entryValues.map((value) => normalizeConfigValue(value)).join('|'),
      entry,
    )
    if (resolved) {
      resolvedMessages.push(resolved)
    }
  }

  const informationMessages = config.informationMessages
  if (informationMessages && typeof informationMessages === 'object') {
    for (const selectedValue of normalizedSelections) {
      const exactKey = Object.keys(informationMessages).find(
        (key) => normalizeConfigValue(key) === normalizeConfigValue(selectedValue),
      )
      if (!exactKey) {
        continue
      }

      const resolved = buildResolvedMessage(
        `map:${normalizeConfigValue(exactKey)}`,
        informationMessages[exactKey],
      )
      if (resolved) {
        resolvedMessages.push(resolved)
      }
    }
  }

  const seen = new Set<string>()
  return resolvedMessages.filter((message) => {
    if (seen.has(message.key)) {
      return false
    }
    seen.add(message.key)
    return true
  })
}

export const resolveRuleValue = (
  rule: AdditionalDependencyRule,
  resolver: FieldValueResolver,
): string | undefined => {
  const baseValue = sanitizeFieldString(resolver(rule.fieldCode))
  if (!baseValue) {
    return undefined
  }

  let resolved = baseValue
  const legacyRule = rule as LegacyDependencyRuleShape
  let timeFieldCandidate =
    rule.timeFieldCode ||
    rule.combineWithFieldCode ||
    legacyRule.timeField ||
    legacyRule.combineWith
  let mergedWithTime = false

  if (timeFieldCandidate) {
    const timeValue = resolver(timeFieldCandidate)
    const combined = mergeDateAndTimeValues(resolved, timeValue)
    if (combined) {
      resolved = combined
      mergedWithTime = true
    }
  } else if (shouldAutoMergeDateTime(rule)) {
    const fallbackTimeCode =
      rule.fieldCode && IMPLICIT_TIME_FIELD_COMPANIONS[rule.fieldCode.toUpperCase()]
    if (fallbackTimeCode) {
      const timeValue = resolver(fallbackTimeCode)
      const combined = mergeDateAndTimeValues(resolved, timeValue)
      if (combined) {
        resolved = combined
        mergedWithTime = true
      }
    }
  }

  if (mergedWithTime) {
    resolved = normalizeDateTimeString(resolved)
  } else if (rule.valueFormat === 'datetime') {
    resolved = normalizeDateTimeString(resolved)
  }

  return resolved
}
